/**
 * Which music a coπ "play …" means (Gordon 2026-10-31). Pure: a MediaIntent
 * from lib/copi-commands + the catalogue (services/copi-library) in, one pick
 * out. Speech is loose — "yes yes yall" must find "02 Yes, yes, Y_all",
 * "cuts and slices" must find "Cuts & Slices", "hip hop" must find "Hip-Hop" —
 * so titles, names and genres are compared as forgiving keys. Speech
 * recognisers only know dictionary words, so a name comes back split or
 * hyphenated ("Leafnuts" → "leaf nut", "Absent Minded" → "absent-minded"):
 * names are also compared with the spaces taken out, a letter or two apart.
 */

import type { MediaIntent } from './copi-commands';

export interface CopiTrack {
  id: string; title: string; artist: string; genres: string[]; bpm?: number;
  audioUrl: string; imageUrl?: string; releaseId?: string; explicit?: boolean;
}
export interface CopiRelease { id: string; title: string; artist: string; genres: string[]; imageUrl?: string; trackIds: string[]; explicit?: boolean }
export interface CopiVideo { id: string; title: string; artist: string; youtubeId: string; imageUrl?: string; genres: string[] }
export interface CopiPlaylist { id: string; title: string; owner?: string; trackCount?: number }
export interface CopiArtist { id: string; name: string; href: string }
export interface CopiLibrary { tracks: CopiTrack[]; releases: CopiRelease[]; videos: CopiVideo[]; playlists: CopiPlaylist[]; artists?: CopiArtist[] }

export type CopiPick =
  | { kind: 'tracks'; tracks: CopiTrack[]; label: string; releaseId?: string }
  | { kind: 'video'; video: CopiVideo }
  | { kind: 'playlist'; playlist: CopiPlaylist }
  | { kind: 'none'; reason: 'notFound' | 'noBpm' | 'empty'; q?: string };

// Spoken numbers come back as words: "two step" = "2 Step", "dos step",
// "deux step" (EN + SV + ES + FR). Articles that double as "one" (un, une,
// una, uno) are left alone — "Un Nouvel Espoir" is not "1 Nouvel Espoir".
const NUMBER_WORDS: Record<string, string> = {
  zero: '0', one: '1', two: '2', three: '3', four: '4', five: '5', six: '6', seven: '7', eight: '8', nine: '9', ten: '10',
  noll: '0', ett: '1', tva: '2', tre: '3', fyra: '4', fem: '5', sex: '6', sju: '7', atta: '8', nio: '9', tio: '10',
  cero: '0', dos: '2', tres: '3', cuatro: '4', cinco: '5', seis: '6', siete: '7', ocho: '8', nueve: '9', diez: '10',
  deux: '2', trois: '3', quatre: '4', cinq: '5', sept: '7', huit: '8', neuf: '9', dix: '10',
};

// Style names as Spanish/French/Swedish speakers say them → the catalogue's
// (English) genre words: "música clásica" = Classical, "électronique" = Electronic.
const GENRE_WORDS: Record<string, string> = {
  clasica: 'classical', clasico: 'classical', classique: 'classical', klassisk: 'classical', klassiskt: 'classical',
  electronica: 'electronic', electronico: 'electronic', electronique: 'electronic', elektronisk: 'electronic',
  cinematica: 'cinematic', cinematografica: 'cinematic', cinematique: 'cinematic', filmmusik: 'cinematic',
  bandasonora: 'soundtrack', bandeoriginale: 'soundtrack', latina: 'latin', latino: 'latin',
  ambiental: 'ambient', experimentale: 'experimental',
};

/** "02 Yes, yes, Y_all" → "yes yes yall"; "Cuts & Slices" → "cuts and slices"; "two step" → "2 step". */
export function matchKey(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ').replace(/['’_]/g, '').replace(/[^a-z0-9]+/g, ' ').trim()
    .split(' ').map(w => NUMBER_WORDS[w] ?? w).join(' ')
    .replace(/^\d{1,2} (?=\S)/, '') // a leading track number
    .replace(/^(the|a|an) /, '');
}

/** Genres compare without spaces/punctuation: "Hip-Hop" = "hip hop", "R&B" = "r and b" = "rnb". */
export function genreKey(s: string): string {
  const k = matchKey(s).replace(/\band\b/g, 'n').replace(/\s+/g, '');
  return k === 'rhythmnblues' ? 'rnb' : GENRE_WORDS[k] || k;
}

function lev(a: string, b: string): number {
  if (Math.abs(a.length - b.length) > 2) return 3;
  const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let diag = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j];
      prev[j] = Math.min(prev[j] + 1, prev[j - 1] + 1, diag + (a[i - 1] === b[j - 1] ? 0 : 1));
      diag = tmp;
    }
  }
  return prev[b.length];
}
const sameWord = (a: string, b: string) =>
  a === b || (a.length >= 4 && b.length >= 4 && lev(a, b) <= (Math.min(a.length, b.length) >= 8 ? 2 : 1));

/** 0…1 — how well a spoken name matches a title/name. ≥ 0.6 counts. */
export function nameScore(spoken: string, title: string): number {
  const q = matchKey(spoken);
  const best = (t: string) => {
    if (!q || !t) return 0;
    if (q === t) return 1;
    const qs = q.replace(/ /g, ''), ts = t.replace(/ /g, '');
    if (qs === ts) return 0.97;
    // "leaf nut" ≈ "Leafnuts": one letter off (two on longer names).
    if (Math.min(qs.length, ts.length) >= 5) {
      const d = lev(qs, ts);
      if (d === 1) return 0.9;
      if (d === 2 && Math.min(qs.length, ts.length) >= 8) return 0.85;
    }
    const [short, long] = q.length <= t.length ? [q, t] : [t, q];
    if (short.length >= 3 && ` ${long} `.includes(` ${short} `)) return 0.6 + 0.35 * (short.length / long.length);
    const qw = q.split(' '), tw = t.split(' ');
    const hits = qw.filter(w => tw.some(x => sameWord(w, x))).length;
    return 0.75 * (hits / Math.max(qw.length, tw.length));
  };
  const full = matchKey(title);
  const bare = matchKey(title.replace(/\s*[([].*?[)\]]\s*/g, ' ')); // "(Radio Version)"
  return Math.max(best(full), bare !== full ? best(bare) * 0.98 : 0);
}

export function genreMatches(spoken: string, genre: string): boolean {
  const s = genreKey(spoken), g = genreKey(genre);
  if (!s || !g) return false;
  if (g === s || (s.length >= 3 && g.includes(s))) return true;
  if (s.length >= 5 && lev(s, g) <= 1) return true; // "Cintematic"
  return matchKey(genre).split(' ').some(w => w.length >= 5 && sameWord(genreKey(w), s));
}

const OK = 0.6;

function shuffle<T>(arr: T[], rand: () => number): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rand() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}

interface PickOptions {
  rand?: () => number; allowExplicit?: boolean; queue?: number;
  /** What's playing now — a random pick never starts with it again. */
  avoidId?: string;
}

export function pickMedia(intent: MediaIntent, lib: CopiLibrary, opts: PickOptions = {}): CopiPick {
  const rand = opts.rand || Math.random;
  const max = opts.queue ?? 30;
  // Tracks picked FOR someone (random, genre, tempo) skip explicit ones for
  // visitors; a title asked for by name always plays (the player age-gates it).
  const clean = lib.tracks.filter(t => opts.allowExplicit || !t.explicit);
  const byId = new Map(lib.tracks.map(t => [t.id, t]));
  const releaseTracks = (r: CopiRelease) => r.trackIds.map(id => byId.get(id)).filter(Boolean) as CopiTrack[];
  const playableReleases = lib.releases.filter(r => releaseTracks(r).length > 0);
  const byArtist = (pool: CopiTrack[], name: string) => pool.filter(t => nameScore(name, t.artist) >= 0.8);
  const artistNames = [...new Set(lib.tracks.map(t => t.artist).filter(Boolean))];
  const genreNames = [...new Set(lib.tracks.flatMap(t => t.genres))];
  const ofGenre = (pool: CopiTrack[], g: string) => pool.filter(t => t.genres.some(x => genreMatches(g, x)));
  // Shuffled picks start with something other than what's playing now.
  const fresh = (tracks: CopiTrack[]) => {
    const i = tracks.findIndex(t => t.id !== opts.avoidId);
    return i > 0 ? [tracks[i], ...tracks.slice(0, i), ...tracks.slice(i + 1)] : tracks;
  };
  const tracksPick = (tracks: CopiTrack[], label: string, ordered = false, releaseId?: string): CopiPick =>
    tracks.length ? { kind: 'tracks', tracks: (ordered ? tracks : fresh(shuffle(tracks, rand))).slice(0, max), label, releaseId } : { kind: 'none', reason: 'empty' };
  const bestOf = <T,>(items: T[], name: (x: T) => string, q: string) => {
    let top: T | null = null, score = 0;
    for (const it of items) { const s = nameScore(q, name(it)); if (s > score) { score = s; top = it; } }
    return { top, score };
  };

  // ── Tempo (optionally within a style) ──
  if (intent.bpm) {
    let pool = clean.filter(t => t.bpm && t.bpm > 0);
    if (intent.genre) pool = ofGenre(pool, intent.genre);
    if (intent.artist) pool = byArtist(pool, intent.artist);
    const near = (tol: number) => pool.filter(t => Math.abs((t.bpm as number) - intent.bpm!) <= tol);
    const hits = near(5).length ? near(5) : near(12);
    if (!hits.length) return { kind: 'none', reason: 'noBpm' };
    return tracksPick(hits, `${intent.bpm} BPM`);
  }

  // ── Random, by kind ──
  if (intent.random && !intent.q) {
    if (intent.kind === 'release') {
      const pool = playableReleases.filter(x => (opts.allowExplicit || !x.explicit));
      const r = shuffle(pool.filter(x => !opts.avoidId || !x.trackIds.includes(opts.avoidId)), rand)[0] || shuffle(pool, rand)[0];
      return r ? tracksPick(releaseTracks(r), r.title, true, r.id) : { kind: 'none', reason: 'empty' };
    }
    if (intent.kind === 'video') {
      const v = shuffle(lib.videos, rand)[0];
      return v ? { kind: 'video', video: v } : { kind: 'none', reason: 'empty' };
    }
    if (intent.kind === 'playlist') {
      const p = shuffle(lib.playlists.filter(x => (x.trackCount ?? 1) > 0), rand)[0];
      return p ? { kind: 'playlist', playlist: p } : { kind: 'none', reason: 'empty' };
    }
    let pool = clean;
    if (intent.genre) {
      // A style first; else the words may name an artist ("a random Leafnuts
      // track") or part of titles.
      const g = intent.genre;
      pool = ofGenre(clean, g);
      if (!pool.length) pool = clean.filter(t => nameScore(g, t.artist) >= 0.8 || ` ${matchKey(t.title)} `.includes(` ${matchKey(g)} `));
      if (!pool.length) return { kind: 'none', reason: 'notFound', q: g };
    }
    if (intent.artist) {
      pool = byArtist(pool, intent.artist);
      if (!pool.length) return { kind: 'none', reason: 'notFound', q: intent.artist };
    }
    const tracks = fresh(shuffle(pool, rand));
    return tracks.length ? { kind: 'tracks', tracks: tracks.slice(0, max), label: intent.genre ? genreLabel(intent.genre, genreNames) : tracks[0].title } : { kind: 'none', reason: 'empty' };
  }

  // ── A style: "genre funk", "hip hop music" ──
  if (intent.genre && !intent.q) {
    const pool = ofGenre(intent.artist ? byArtist(clean, intent.artist) : clean, intent.genre);
    return pool.length ? tracksPick(pool, genreLabel(intent.genre, genreNames)) : { kind: 'none', reason: 'notFound', q: intent.genre };
  }

  // ── An artist: "songs by Desmond Foster" ──
  if (intent.kind === 'artist') {
    const name = intent.artist || intent.q || '';
    const { top, score } = bestOf(artistNames, a => a, name);
    if (!top || score < OK) return { kind: 'none', reason: 'notFound', q: name };
    return tracksPick(byArtist(lib.tracks, top), top);
  }

  const q = intent.q || '';
  const trackPool = intent.artist ? byArtist(lib.tracks, intent.artist) : lib.tracks;
  const releasePool = intent.artist ? lib.releases.filter(r => nameScore(intent.artist!, r.artist) >= 0.8) : lib.releases;
  const trackBest = bestOf(trackPool, t => t.title, q);
  const releaseBest = bestOf(playableReleases.filter(r => releasePool.includes(r)), r => r.title, q);
  const videoBest = bestOf(lib.videos, v => v.title, q);
  const listBest = bestOf(lib.playlists, p => p.title, q);
  // A song asked for by name, then the rest of its release, then more by the
  // same artist or in the same style — so "next" has somewhere to go.
  const playTrack = (t: CopiTrack): CopiPick => {
    const rel = t.releaseId ? lib.releases.find(r => r.id === t.releaseId) : undefined;
    const relTracks = rel ? releaseTracks(rel) : [];
    const after = relTracks.slice(relTracks.findIndex(x => x.id === t.id) + 1);
    const seen = new Set([t.id, ...after.map(x => x.id)]);
    const more = shuffle(clean.filter(x => !seen.has(x.id) && ((!!t.artist && x.artist === t.artist) || x.genres.some(g => t.genres.includes(g)))), rand);
    return { kind: 'tracks', tracks: [t, ...after, ...more].slice(0, max), label: t.artist ? `${t.title} — ${t.artist}` : t.title, releaseId: t.releaseId };
  };
  const playRelease = (r: CopiRelease) => tracksPick(releaseTracks(r), r.title, true, r.id);

  // "Despacito de Luis Fonsi", "Alors on danse de Stromae", "X por Y", "X par Y":
  // Spanish and French name the artist after "de"/"por"/"par" — but titles
  // have "de" in them too, so only a split that finds that artist AND that
  // title counts, and only when the whole words aren't already a title.
  if (!intent.artist && q && intent.kind !== 'playlist' && Math.max(trackBest.score, releaseBest.score, videoBest.score) < 0.97) {
    const ws = q.split(/\s+/);
    for (let i = ws.length - 2; i >= 1; i--) {
      if (!/^(de|del|du|des|d|por|par|di|da|von|van)$/i.test(ws[i])) continue;
      const artist = ws.slice(i + 1).join(' ');
      if (!artistNames.some(a => nameScore(artist, a) >= 0.8)) continue;
      const sub = pickMedia({ ...intent, q: ws.slice(0, i).join(' '), artist, random: undefined }, lib, opts);
      if (sub.kind !== 'none') return sub;
    }
  }

  if (intent.kind === 'track') return trackBest.top && trackBest.score >= OK ? playTrack(trackBest.top) : { kind: 'none', reason: 'notFound', q };
  if (intent.kind === 'release') return releaseBest.top && releaseBest.score >= OK ? playRelease(releaseBest.top) : { kind: 'none', reason: 'notFound', q };
  if (intent.kind === 'video') return videoBest.top && videoBest.score >= OK ? { kind: 'video', video: videoBest.top } : { kind: 'none', reason: 'notFound', q };
  if (intent.kind === 'playlist') return listBest.top && listBest.score >= OK ? { kind: 'playlist', playlist: listBest.top } : { kind: 'none', reason: 'notFound', q };

  // ── "play <anything>": the best match of every kind wins; on a tie the
  //    artist, then the track, then the release, then the style. ──
  const artistBest: { top: string | null; score: number } = intent.artist ? { top: null, score: 0 } : bestOf(artistNames, a => a, q);
  const genreHit = genreNames.filter(g => genreMatches(q, g));
  const genreScore = genreHit.some(g => genreKey(g) === genreKey(q)) ? 0.99 : genreHit.length ? 0.85 : 0;
  const options: [number, () => CopiPick][] = [
    [artistBest.score, () => tracksPick(byArtist(lib.tracks, artistBest.top!), artistBest.top!)],
    [trackBest.score, () => playTrack(trackBest.top as CopiTrack)],
    [releaseBest.score, () => playRelease(releaseBest.top as CopiRelease)],
    [genreScore, () => tracksPick(ofGenre(clean, q), genreLabel(q, genreNames))],
    [videoBest.score, () => ({ kind: 'video', video: videoBest.top as CopiVideo })],
    [listBest.score, () => ({ kind: 'playlist', playlist: listBest.top as CopiPlaylist })],
  ];
  let win: [number, () => CopiPick] | null = null;
  for (const o of options) if (o[0] >= OK && (!win || o[0] > win[0] + 0.001)) win = o;
  return win ? win[1]() : { kind: 'none', reason: 'notFound', q };
}

/** The catalogue's own spelling of a spoken style ("hip hop" → "Hip-Hop"). */
function genreLabel(spoken: string, genres: string[]): string {
  return genres.find(g => genreKey(g) === genreKey(spoken)) || genres.find(g => genreMatches(spoken, g)) || spoken;
}

export interface CopiEntity { kind: 'artist' | 'release' | 'track' | 'video'; name: string; href: string; score: number }

/** The page a spoken name points at — "Leafnuts" → the artist page. Only a
 *  clear match (≥ `min`) counts; on a tie the artist, then the release, the
 *  track, the video. */
export function findEntity(spoken: string, lib: CopiLibrary, kind?: CopiEntity['kind'], min = 0.9): CopiEntity | null {
  const all: CopiEntity[] = [
    ...(lib.artists || []).map(a => ({ kind: 'artist' as const, name: a.name, href: a.href, score: 0 })),
    ...lib.releases.map(r => ({ kind: 'release' as const, name: r.title, href: `/music/${r.id}`, score: 0 })),
    ...lib.tracks.map(t => ({ kind: 'track' as const, name: t.title, href: `/music/${t.id}`, score: 0 })),
    ...lib.videos.map(v => ({ kind: 'video' as const, name: v.title, href: `/videos/${v.id}`, score: 0 })),
  ];
  let best: CopiEntity | null = null;
  for (const e of all) {
    if (kind && e.kind !== kind) continue;
    const score = nameScore(spoken, e.name);
    if (score >= min && (!best || score > best.score + 0.001)) best = { ...e, score };
  }
  return best;
}

/** A page "show me <name>" can open that isn't music: a person, a label, a
 *  shop item or an event (services/copi-library loads them). */
export interface CopiPlace { kind: 'person' | 'label' | 'item' | 'event'; name: string; href: string }
type ShowKind = 'artist' | 'release' | 'track' | 'video' | 'playlist' | 'label' | 'person' | 'item' | 'event';

/** "show me <name>" (Gordon 2026-11-02) — the best page across the catalogue
 *  and the directory. Only a clear match (≥ `min`) counts; on a tie the
 *  artist, then the person, label, release, track, video, playlist, item, event. */
export function findPlace(spoken: string, lib: CopiLibrary, places: CopiPlace[], kind?: ShowKind, min = 0.9): { kind: ShowKind; name: string; href: string; score: number } | null {
  const all: { kind: ShowKind; name: string; href: string }[] = [
    ...(lib.artists || []).map(a => ({ kind: 'artist' as const, name: a.name, href: a.href })),
    ...places.filter(p => p.kind === 'person'),
    ...places.filter(p => p.kind === 'label'),
    ...lib.releases.map(r => ({ kind: 'release' as const, name: r.title, href: `/music/${r.id}` })),
    ...lib.tracks.map(t => ({ kind: 'track' as const, name: t.title, href: `/music/${t.id}` })),
    ...lib.videos.map(v => ({ kind: 'video' as const, name: v.title, href: `/videos/${v.id}` })),
    ...lib.playlists.map(p => ({ kind: 'playlist' as const, name: p.title, href: `/playlist/${p.id}` })),
    ...places.filter(p => p.kind === 'item' || p.kind === 'event'),
  ];
  let best: { kind: ShowKind; name: string; href: string; score: number } | null = null;
  for (const e of all) {
    if (kind && e.kind !== kind) continue;
    const score = nameScore(spoken, e.name);
    if (score >= min && (!best || score > best.score + 0.001)) best = { ...e, score };
  }
  return best;
}

/** The catalogue's own spelling of a style named exactly ("hip hop" → "Hip-Hop"), or null. */
export function styleNamed(spoken: string, lib: CopiLibrary): string | null {
  const key = genreKey(spoken);
  if (!key) return null;
  for (const g of [...lib.releases.flatMap(r => r.genres), ...lib.tracks.flatMap(t => t.genres)]) if (genreKey(g) === key) return g;
  return null;
}
